import React, { useEffect, useState } from "react";
import { clickable } from "../utils/a11y";
import { useDialog } from "../utils/useDialog";
import "../styles/login.css"
import axios from 'axios';
import Cookies from "js-cookie";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "../utils/toast";
import { useSubmitGuard } from "../utils/useSubmitGuard";
import { errorMessage } from "../utils/errorMessage";
import Loader from "../utils/loader";
import { API_DETAILS, COOKIE_KEYS } from "../App";
import { getSeverityTier, hasSymptomScore } from "../utils/symptomSeverity";
import { isFreeTextQuestion, isMultiSelectQuestion, followUpOptionId } from "../utils/questionnaireQuestions";
import { isMedicationQuestion } from "../utils/flaggedMedications";
import MedicationNotice from "./MedicalProfile/MedicationNotice";
import IntakePharmacyPicker from "./MedicalProfile/IntakePharmacyPicker";

// Shown beside the marketing checkbox and stored with each consent. Keep identical
// to PatientService.MARKETING_CONSENT_TEXT in the backend: the record is only worth
// having if it says exactly what the person agreed to.
const MARKETING_CONSENT_TEXT = "Send me news and offers from Youro. You can unsubscribe at any time.";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Assessment = () => {
    const name = useParams().name?.split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ").replace(/\bUtis\b/, "UTIs");
    const navigate = useNavigate();
    const location = useLocation();

    let { apptId, diagId } = location.state || {};
    let uId = Cookies.get(COOKIE_KEYS.userId);
    const [open, setOpen] = useState(true);
    const [chooseDiagnosis, setChooseDiagnosis] = useState(false);

    // SC SymptomCalculator
    const [selDiag, setDiagName] = useState("");
    const [qId, setQId] = useState(0);
    const [selectedOption, setSelectedOption] = useState(-1);
    const [isBackNavigation, setIsBackNavigation] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    // A failed questionnaire load used to leave an empty card with no
    // explanation and nothing to press.
    const [questionLoadFailed, setQuestionLoadFailed] = useState(false);
    const [questionRefusal, setQuestionRefusal] = useState(null);
    const [emailing, guard] = useSubmitGuard();
    const [questionnaire, setQuestionnaire] = useState([]);
    const [listAllQuestion, setListAllQuestion] = useState(false);
    const [textResponses, setTextResponses] = useState({});
    const [isMultiOption, setIsMultiOption] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [currentQid, setCurrentQid] = useState(0);
    // True while a pharmacy chosen from the search is being saved to the profile.
    const [pharmacySaving, setPharmacySaving] = useState(false);
    const [userResponse, setUserResponse] = useState([]);

    useEffect(() => {
        if (name) fetchQuesByDiagId();
    }, [name, qId, isBackNavigation]);

    const [showEmailCapture, setShowEmailCapture] = useState(false);
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    // Unticked by default: marketing use of questionnaire emails needs explicit
    // consent under the Notice of Privacy Practices.
    const [marketingOptIn, setMarketingOptIn] = useState(false);
    // Set once the public questionnaire is submitted; the score goes by email.
    const [emailSentTo, setEmailSentTo] = useState("");

    // The score, shown to a signed-in patient before they are sent back to their
    // home page. A dozen questions about how much their symptoms interfere with
    // their life produced a number that was stored, shown to the provider, and
    // never shown to them — they got "Questionnaire submitted successfully!" and
    // a redirect. Matches what the mobile app now shows at the same moment.
    //
    // Signed-in patients only. A visitor taking the public questionnaire from a
    // condition page receives their score by email instead.
    const [scoreResult, setScoreResult] = useState(null);
    // No close handler on purpose: the result is dismissed only by its button.
    // The hook still moves focus into it and keeps Tab inside it.
    const scoreDialogRef = useDialog(Boolean(scoreResult));
    const scoreTier = scoreResult ? getSeverityTier(Number(scoreResult.score)) : null;


    const optionButtonStyle = {
        width: "100%",
        padding: "16px",
        textAlign: "left",
        borderRadius: "8px",
        border: "1px solid #e5e7eb",
        backgroundColor: "#ffffff",
        cursor: "pointer",
        transition: "all 0.2s ease",
        fontSize: "0.875rem",
        color: "#374151",
        whiteSpace: "normal",
        wordWrap: "break-word",
        lineHeight: "1.4"
    };

    // Helper functions for the hover effect
    const handleMouseEnter = (e) => {
        e.currentTarget.style.borderColor = "#8fa87a";
        e.currentTarget.style.backgroundColor = "#cfe8d4";
        e.currentTarget.style.transform = "scale(1.01)";
    };

    const handleMouseLeave = (e, isSelected = false) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = "#ffffff";
        }
        e.currentTarget.style.borderColor = "#e5e7eb";
        e.currentTarget.style.transform = "scale(1)";
    };
    const handleBackendOptionClick = (optionId, optionName, isCheckbox = false) => {
        const currentQ = questionnaire[0];

        setUserResponse(prev => {
            const filtered = prev.filter(r => r.qId !== currentQ.questionId);
            let optionsData = [];

            if (isCheckbox) {
              // Get existing options for this question if any
              const existing = prev.find(r => r.qId === currentQ.questionId);
              const existingOptions = existing ? existing.optionsData : [];

              if (existingOptions.some(opt => opt.oId === optionId)) {
                optionsData = existingOptions.filter(opt => opt.oId !== optionId);
              } else {
                optionsData = [...existingOptions, { oId: optionId, oName: optionName }];
              }
            } else {
              // Standard radio or text input
              optionsData = [{ oId: optionId, oName: optionName }];
            }

            const newResponse = {
              qId: currentQ.questionId,
              question: currentQ.question,
              optionsData: optionsData
            };

            return [...filtered, newResponse];
        });

        // Only move to next question if it's NOT a checkbox or text field
        if (!isCheckbox && optionId !== -100) {
          // 2. Update states to trigger the next API call via useEffect
          setSelectedOption(optionId);
          setQId(currentQ.questionId);
          setIsBackNavigation(0); // Ensure we are moving forward
          // Clear current question to show loader
          setQuestionnaire([]);
        }
    };


    // --- State Management ---
    const [step, setStep] = useState(1);
    const [assessmentResults, setAssessmentResults] = useState({});

    const totalSteps = 8;

    // --- Questions Data ---
    const questions = {
        1: {
            id: "biologicalSex",
            title: "What is your biological sex?",
            description: "Select the option that best describes your situation",
            options: ["Biological male", "Biological female"]
        },
        2: {
            id: "bph",
            title: "Do you experience difficulty starting urination, weak stream, incomplete emptying, or increased nighttime urination?",
            description: "Select the option that best describes your situation",
            options: ["No symptoms", "Mild symptoms - occasional hesitancy or weak flow", "Moderate symptoms - daily difficulty with urination", "Severe symptoms - frequent straining, dribbling, or retention"]
        },
        3: {
            id: "oab",
            title: "Do you have sudden, urgent needs to urinate with frequency, nocturia, or urge incontinence?",
            description: "Select the option that best describes your situation",
            options: ["No symptoms", "Mild symptoms - occasional urgency without leakage", "Moderate symptoms - daily urgency with some leakage", "Severe symptoms - frequent accidents and pad usage"]
        },
        4: {
            id: "uti",
            title: "Do you experience burning during urination, cloudy urine, pelvic pain, or frequent UTIs?",
            description: "Select the option that best describes your situation",
            options: ["No symptoms", "Mild symptoms - 1-2 UTI episodes per year", "Moderate symptoms - 3-4 UTI episodes per year", "Severe symptoms - 5+ UTIs per year or chronic symptoms"]
        },
        5: {
            id: "stones",
            title: "Have you experienced severe flank pain, pain in the abdomen radiating to the groin, or blood in the urine?",
            description: "Select the option that best describes your situation",
            options: ["No symptoms", "Mild symptoms - history of kidney stones but no recent episodes", "Moderate symptoms - occasional kidney stone episodes with pain", "Severe symptoms - frequent kidney stone episodes requiring medical care"]
        },
        6: {
            id: "ed",
            title: "Do you have difficulty achieving erections, maintaining firmness, or reduced morning erections?",
            description: "Select the option that best describes your situation",
            options: ["No symptoms", "Mild symptoms - occasionally soft or difficult to achieve", "Moderate symptoms - frequently unable to maintain firmness", "Severe symptoms - rarely able to achieve adequate erection"]
        },
        7: {
            id: "pe",
            title: "Do you ejaculate within 1-2 minutes of penetration, have poor ejaculatory control, or partner dissatisfaction?",
            description: "Select the option that best describes your situation",
            options: ["No symptoms", "Mild symptoms - occasionally finish too quickly", "Moderate symptoms - frequently ejaculate within 2 minutes", "Severe symptoms - almost always ejaculate within 1 minute"]
        },
        8: {
            id: "lowT",
            title: "Do you experience fatigue, decreased libido, mood changes, muscle loss, or difficulty concentrating?",
            description: "Select the option that best describes your situation",
            options: ["No symptoms", "Mild symptoms - some fatigue and decreased energy", "Moderate symptoms - noticeable mood and energy changes", "Severe symptoms - significant impact on daily functioning"]
        }
    };

    // --- Result Mapping Logic ---
    const calculateResults = () => {
        const displayMap = {
            bph: "Enlarged Prostate (BPH)",
            oab: "Overactive Bladder",
            uti: "Recurrent UTIs",
            stones: "Recurrent Kidney Stones",
            ed: "Erectile Dysfunction",
            pe: "Premature Ejaculation",
            lowT: "Low Testosterone"
        };

        const findings = [];

        Object.keys(assessmentResults).forEach((key) => {
            const answer = assessmentResults[key];
            if (!displayMap[key] || !answer || answer.startsWith("No")) return;

            let severity = "Mild";
            let styles = { bg: "#fef9c3", text: "#854d0e" }; // Default Yellow

            if (answer.includes("Moderate")) {
                severity = "Moderate";
                styles = { bg: "#ffedd5", text: "#9a3412" }; // Orange
            } else if (answer.includes("Severe")) {
                severity = "Severe";
                styles = { bg: "#fee2e2", text: "#b91c1c" }; // Red
            }

            findings.push({
                label: displayMap[key],
                severity: severity,
                color: styles.bg,
                textColor: styles.text
            });
        });

        return findings;
    };

    const urlMap = {
        "Enlarged Prostate (BPH)": "/conditions/enlarged-prostate",
        "Overactive Bladder": "/conditions/overactive-bladder",
        "Erectile Dysfunction": "/conditions/erectile-dysfunction",
        "Recurrent UTIs": "/conditions/recurrent-utis",
        "Recurrent Kidney Stones": "/conditions/recurrent-kidney-stones",
        "Premature Ejaculation": "/conditions/premature-ejaculation",
        "Low Testosterone": "/conditions/low-testosterone"
    };

    const results = calculateResults();

    // 2. Use that array to determine the UI configuration
    const config = React.useMemo(() => {
        const hasModSev = results.some(r => r.severity === "Moderate" || r.severity === "Severe");
        const hasMild = results.some(r => r.severity === "Mild");

        if (hasModSev)
            return {
                title: "High Priority",
                description: "Based on your responses, you're experiencing significant urologic symptoms that warrant professional evaluation. Our specialists can help provide effective treatment options.",
                steps: ["Schedule a consultation with a urologic specialist", "Prepare a symptom diary for your appointment", "Consider same-day consultation if symptoms are severe"],
                bgColor: "#fef2f2",
                borderColor: "#ffc9c9",
                titleColor: "#dc2626"
            };
        if (hasMild)
            return {
                title: "Moderate Priority",
                description: "You're experiencing some urologic symptoms that could benefit from professional guidance. Early intervention often leads to better outcomes.",
                steps: ["Schedule a consultation within the next few weeks", "Track your symptoms and their frequency", "Consider lifestyle modifications that may help"],
                bgColor: "#fff7ed",
                borderColor: "#fed7aa",
                titleColor: "#ea580c"
            };
        return {
            title: "Preventive Care",
            description: "Great news! You're not experiencing significant urologic symptoms. Consider preventive care to maintain your urologic health.",
            steps: ["Schedule routine preventive screenings as appropriate", "Maintain healthy lifestyle habits", "Stay aware of any changes in urologic health"],
            bgColor: "#f0fdf4",
            borderColor: "#bbf7d0",
            titleColor: "#16a34a"
        };
    }, [results]);

    // --- Handlers ---
    const handleOptionClick = (option) => {
        const currentQuestion = questions[step];
        const updatedResults = { ...assessmentResults, [currentQuestion.id]: option };
        setAssessmentResults(updatedResults);

        let nextStep = step + 1;

        // --- Branching Logic ---
        if (currentQuestion.id === "biologicalSex") {
            if (option === "Biological female") {
                nextStep = 3; // Skip BPH (Step 2)
            }
        }
        else if (updatedResults.biologicalSex === "Biological female" && step === 5) {
            nextStep = 9; // After Stones (Step 5), skip Male-only questions and go to Results
        }

        // --- Navigation Logic ---
        if (nextStep <= totalSteps) {
            setStep(nextStep);
        } else {
            setStep(9); // Show results
        }

        window.scrollTo(0, 0);
    };

    const handlePrevious = () => {
        // A chosen pharmacy is still saving: moving on now could let the intake
        // be submitted while it saves, and the two writes overwrite each other.
        if (pharmacySaving) return;
        // --- FLOW A: STATIC QUESTIONS (!name) ---
        if (!name) {
            if (step === 1) return;
            let prevStep = step - 1;
            if (assessmentResults.biologicalSex === "Biological female") {
                if (step === 3) prevStep = 1;
                if (step === 9) prevStep = 5;
            }
            setStep(prevStep);
            return;
        }

        // --- FLOW B: BACKEND QUESTIONS ---
        if (userResponse.length === 0) {
            navigate("/");
            return;
        }

        const responses = [...userResponse];

        if (step === 9) {
            // CASE: Back from Results Page
            // We want to re-display the very last question the user answered.
            const lastQuestionAnswered = responses[responses.length - 1];

            // 1. Set the questionnaire to the last saved state
            setQuestionnaire([{
                questionId: lastQuestionAnswered.qId,
                question: lastQuestionAnswered.question,
                options: [] // The API will need to re-fetch these, or you can store them in history
            }]);

            // 2. Set the pointers to this question so fetchQuesByDiagId fetches it specifically
            setQId(responses.length > 1 ? responses[responses.length - 2].qId : 0);
            setSelectedOption(responses.length > 1 ? responses[responses.length - 2].optionsData[0].oId : -1);
            setIsBackNavigation(0); // We are "moving forward" from the perspective of the second-to-last question

            // 3. Remove the last response from history so it can be "re-answered"
            responses.pop();
            setUserResponse(responses);

            // 4. Return to the question view
            setStep(1);
        } else {
            // CASE: Normal Back (Question to Question)
            const currentQuestionId = questionnaire[0]?.questionId;
            const lastAnswered = responses.pop();

            setUserResponse(responses);
            setQId(currentQuestionId);
            setSelectedOption(lastAnswered.optionsData[0].oId);
            setIsBackNavigation(1);
            setQuestionnaire([]);
        }

        window.scrollTo(0, 0);
    };

    // --- UI Shared Styles ---
    const cardBase = {
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
        marginBottom: "24px"
    };

    const progressPercentage = assessmentResults.biologicalSex === "Biological female"
        ? (step === 1 ? 25 : step === 3 ? 50 : step === 4 ? 75 : 100)
        : (step / totalSteps) * 100;

    // SC SymptomCalculator

    const fetchQuesByDiagId = async () => {
        // TODO: After logout, cookie of the userId still exists
        if (!apptId && name !== "Intake Form") {
          uId = 0;
          diagId = 0;
        }

        const temp = {
          questionnaire_type: name,
          last_question_id: qId,
          selected_option_id: selectedOption,
          patient_id: uId,
          diagId: diagId,
          is_back_navigation: isBackNavigation,
          // The appointment decides Initial or Follow-up, and the server says up
          // front if this one can't be taken (not open yet, closed, already
          // done) instead of after the last answer. Never for the intake form,
          // which isn't tied to the appointment's window.
          appointment_id: apptId && name !== "Intake Form" ? apptId : 0,
        };
        const url =
          API_DETAILS.baseUrl +
          API_DETAILS.PORT +
          API_DETAILS.baseExtension +
          "/getQuestionsByQuestionnaireType";

        setIsLoading(true);
        axios
          .post(url, temp)
          .then((res) => {
            if (res.data && res.data.length > 0) {
                const firstQ = res.data[0];
                setQuestionnaire(res.data);
                setListAllQuestion(isFreeTextQuestion(firstQ));
                // 279 is production's medical-history question, which this page
                // has always treated as multi-select. Kept alongside the wording
                // check so the fix cannot turn it into a single choice if its
                // text lacks "check all that apply".
                setIsMultiOption(isMultiSelectQuestion(firstQ) || firstQ.questionId === 279);
                setCurrentQid(firstQ.questionId);
                const prevResponse = userResponse.find(
                  (response) => response.qId === firstQ.questionId
                );

                if (prevResponse) {
                  const opt = prevResponse.optionsData[0];
                  setSelectedOption(opt?.oId || 0);
                  if (opt?.oId === -100) {
                    setTextResponses(prev => ({ ...prev, [firstQ.questionId]: opt.oName }));
                  }
                }
            } else {
                if (name && (!apptId && name !== "Intake Form")) {
                  setShowEmailCapture(true); // Show your new element
                } else if (name && (apptId || name === "Intake Form")) {
                  handleEmailSubmit();
                } else {
                    setStep(9); // Static flow still goes to results
                }
            }
          })
          .catch((err) => {
            // A refusal the server explains (e.g. "opens on Tue, Oct 6") is
            // shown as it is; anything else is a failure to load.
            const refusal = err?.response && err.response.status < 500 ? errorMessage(err, null) : null;
            setQuestionRefusal(refusal);
            setQuestionLoadFailed(true);
          })
          .finally(() => {
            setIsLoading(false);
          });
    };

    const saveNewSymptomScore = async (data) => {
        let api = (!apptId && name !== "Intake Form") ? "/saveScore" : "/saveSymptomScore";
        const url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + api;

        const config = {
          headers: {
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "*",
            "Content-Type": "application/json",
          },
        };

        try {
          const res = await axios.post(url, data, config);
//          setDiagnoses(res.data);
//          console.log("Saved Score::::::::::::::::", res.data);
//          setNewScoreInfo(res.data);
//          setSeverityDescription(getSeverityDescription(res.data.score));
//          if (diagId != 0 && !props.fromLandingPage) setSymptomScorePage(true);
//          else props.setOpen(false);
          return res.data;
        } catch (err) {
          throw err;
        }
      };

    const handleEmailSubmit = guard(async () => {
        const isPatientFlow = (apptId || name === "Intake Form");  // TODO: !!uId && .....
        if (!isPatientFlow && !EMAIL_PATTERN.test(email.trim())) {
            setEmailError("Please enter a valid email address.");
            return;
        }
        setIsLoading(true);

        let payload = {};
        if (isPatientFlow) {
          payload = {
            diagnosisId: diagId,
            questionData: userResponse,
            appointmentId: apptId || 0,
            patientId: parseInt(uId),
          };
        } else {
          payload = {
            questionData: userResponse,
            email: email.trim(),
            questionnaire_type: name,
            diagnosisId: null,
            marketingOptIn,
          };
        }
        try {
          const result = await saveNewSymptomScore(payload);

          if (isPatientFlow) {
            // Hold the redirect until the patient has seen their score. Only
            // when there is a real one: 0 is this codebase's "no score
            // recorded" sentinel, and hasSymptomScore rejects it, so an
            // unrecognised value cannot be dressed up as a severity tier.
            if (hasSymptomScore(Number(result?.score))) {
              setScoreResult(result);
              setIsLoading(false);
              return;
            }
            toast.success("Questionnaire submitted. Taking you to your dashboard.");
            setTimeout(() => {
              navigate("/patient-home");
            }, 1000);
          } else {
            // The same confirmation whether or not this address has taken a
            // questionnaire before: the server answers both alike, so the page
            // cannot reveal who has.
            setEmailSentTo(email.trim());
            setIsLoading(false);
          }
        } catch (err) {
          // 400 and 429 carry a message written for the visitor (an email that could
          // not be sent, too many attempts); errorMessage passes those through.
          toast.error(errorMessage(err, "We couldn't submit your questionnaire. Check your answers and try again."));
          setIsLoading(false);
        }
    });

    return (
        <div className="assessment-root" style={{ backgroundColor: step > totalSteps ? "rgba(207, 232, 212, 0.3)" : "transparent", transition: "background-color 0.5s ease" }}>
            <Loader active={isLoading} />
            <header className="home-nav">
                <Link to="/" className="home-nav-left" aria-label="Youro home">
                    <div className="home-nav-logo-circle">Y</div>
                    <span className="home-nav-logo-text">Youro</span>
                </Link>
                <nav className="home-nav-center">
                    <button className="home-nav-link" onClick={() => navigate("/about")}>About</button>
                    <button className="home-nav-link" onClick={() => navigate("/conditions")}>Find Your Diagnosis</button>
                    <button className="home-nav-link" onClick={() => navigate("/assessment")}>Symptom Assessment</button>
                </nav>
                <div className="home-nav-right">
                    <button className="home-nav-signin" onClick={() => navigate("/login")}>Sign In</button>
                    <button className="home-nav-get-started" onClick={() => navigate("/signup")}>Get Started</button>
                </div>
            </header>

            <main className="login-main" style={{ paddingTop: "28px" }}>
                <div style={{ maxWidth: "42rem", width: "100%", marginLeft: "auto", marginRight: "auto", paddingLeft: "1rem", paddingRight: "1rem" }}>

                    {/* Back to Home / Back Nav */}
                    <button
                        className="login-back"
                        type="button"
                        onClick={() => {
                            // If we have history (backend) or are past step 1 (static), go back.
                            // Otherwise, go home.
                            if (name) {
                                userResponse.length > 0 ? handlePrevious() : navigate("/");
                            } else {
                                step === 1 ? navigate("/") : handlePrevious();
                            }
                        }}
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", borderRadius: "8px", fontSize: "0.875rem", fontWeight: 500, height: "36px", padding: "0 1rem", marginBottom: "1.5rem", cursor: "pointer", border: "none", background: "transparent", color: "#374151" }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m12 19-7-7 7-7" /><path d="M19 12H5" />
                        </svg>
                        {(name && userResponse.length > 0) || (!name && step > 1)
                                ? "Previous Question"
                                : "Back to Home"}
                    </button>

                    { step <= totalSteps && !showEmailCapture ? (
                        <>
                            {/* Progress Card */}
                            <div style={{ ...cardBase, padding: "24px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                                    <h4 style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0, color: "#111827" }}>{name ? `${name} Questionnaire` : "Urologic Health Assessment"}</h4>
                                    { !name && (
                                        <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                                            {assessmentResults.biologicalSex === "Biological female" && step > 1
                                                ? (step === 3 ? 2 : step === 4 ? 3 : 4) // Show 2, 3, 4 for females
                                                : step} of {assessmentResults.biologicalSex === "Biological female" ? 4 : totalSteps}
                                        </span>
                                    )}
                                </div>
                                { !name && (
                                    <div style={{ backgroundColor: "#f3f4f6", height: "8px", borderRadius: "4px", width: "100%", overflow: "hidden" }}>
                                        <div style={{
                                            backgroundColor: "#8fa87a",
                                            height: "100%",
                                            width: `${progressPercentage}%`,
                                            transition: "width 0.3s ease"
                                        }}></div>
                                    </div>
                                )}
                            </div>

                            {/* Question Card */}
                            <div style={cardBase}>
                                {!isLoading && questionLoadFailed && questionRefusal && (
                                    <div style={{ padding: "24px", textAlign: "center" }}>
                                        <h4 style={{ margin: "0 0 8px 0" }}>This questionnaire isn't available</h4>
                                        <p style={{ margin: "0 0 16px 0", color: "var(--youro-muted)" }}>
                                            {questionRefusal}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => navigate("/patient-home")}
                                            style={{ padding: "10px 16px", borderRadius: "8px", border: "1px solid var(--youro-sky)", backgroundColor: "transparent", color: "var(--youro-sky)", cursor: "pointer", fontWeight: 600 }}
                                        >
                                            Back to your appointments
                                        </button>
                                    </div>
                                )}

                                {!isLoading && questionLoadFailed && !questionRefusal && (
                                    <div style={{ padding: "24px", textAlign: "center" }}>
                                        <h4 style={{ margin: "0 0 8px 0" }}>We couldn't load your questions</h4>
                                        <p style={{ margin: "0 0 16px 0", color: "var(--youro-muted)" }}>
                                            Nothing you have answered so far is lost. Try again in a moment.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => window.location.reload()}
                                            style={{ padding: "10px 16px", borderRadius: "8px", border: "1px solid var(--youro-sky)", backgroundColor: "transparent", color: "var(--youro-sky)", cursor: "pointer", fontWeight: 600 }}
                                        >
                                            Try again
                                        </button>
                                    </div>
                                )}

                                {/* Only show content if we are NOT loading */}
                                {!isLoading && !questionLoadFailed && (
                                    <>
                                        <div style={{ padding: "24px" }}>
                                            <h4 style={{ fontSize: "1.125rem", fontWeight: 600, lineHeight: "1.6", color: "#111827", margin: "0 0 8px 0", whiteSpace: "normal", overflowWrap: "break-word", textAlign: "left" }}>
                                                {/* Priority: Backend Question -> Hardcoded Question */}
                                                {questionnaire.length > 0
                                                    ? questionnaire[0].question
                                                    : (!name ? questions[step].title : "")}
                                            </h4>
                                            {/* While listing their medications, the ones the provider
                                                must know about before prescribing. Intake form only. */}
                                            {name === "Intake Form" && questionnaire.length > 0 &&
                                              isMedicationQuestion(questionnaire[0].question) && <MedicationNotice />}
                                            {/* Only show description for hardcoded flow */}
                                            {!name && questionnaire.length === 0 && (
                                                <p style={{ fontSize: "0.875rem", color: "#6b7280", margin: 0 }}>
                                                    {questions[step].description}
                                                </p>
                                            )}
                                        </div>

                                        <div style={{ padding: "0 24px 24px 24px", display: "flex", flexDirection: "column", gap: "12px" }}>
                                            {questionnaire.length > 0 ? (
                                              // 1. RENDER BACKEND OPTIONS
                                              listAllQuestion ? (
                                                /* RENDER TEXT INPUT */
                                                <div style={{ width: "100%" }}>
                                                  {/* The pharmacy follow-up: search the directory, or type. */}
                                                  {name === "Intake Form" && /preferred pharmacy/i.test(questionnaire[0].question) && (
                                                    <IntakePharmacyPicker
                                                      patientId={uId}
                                                      onSaving={setPharmacySaving}
                                                      onChosen={(text) => {
                                                        setTextResponses(prev => ({ ...prev, [questionnaire[0].questionId]: text }));
                                                        handleBackendOptionClick(-100, text);
                                                      }}
                                                    />
                                                  )}
                                                  <input
                                                    type="text"
                                                    placeholder="Type your response here..."
                                                    value={textResponses[questionnaire[0].questionId] || ''}
                                                    onChange={(e) => {
                                                      const val = e.target.value;
                                                      setTextResponses(prev => ({ ...prev, [questionnaire[0].questionId]: val }));
                                                      handleBackendOptionClick(-100, val);
                                                    }}
                                                    style={{
                                                      width: "100%",
                                                      padding: "14px",
                                                      borderRadius: "8px",
                                                      border: "1px solid #e5e7eb",
                                                      fontSize: "0.875rem",
                                                    }}
                                                  />
                                                  <button
                                                    disabled={pharmacySaving}
                                                    aria-busy={pharmacySaving}
                                                    onClick={() => {
                                                      if (pharmacySaving) return;
                                                      const currentInput = textResponses[questionnaire[0].questionId];
                                                      if (!currentInput || currentInput.trim() === "") {
                                                        toast.info("Add a response, or skip this question for now.");
                                                        return;
                                                      }
                                                      // A typed answer has no option, so nothing hangs off it.
                                                      // Without this the previous question's option went with it.
                                                      setSelectedOption(0);
                                                      setQId(questionnaire[0].questionId);
                                                      setIsBackNavigation(0);
                                                      setQuestionnaire([]);
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      e.currentTarget.style.transform = "scale(1.03)";
                                                      e.currentTarget.style.backgroundColor = 'var(--youro-coral)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = "scale(1)";
                                                        e.currentTarget.style.backgroundColor = '#8fa87a';
                                                    }}
                                                    style={{ ...optionButtonStyle, marginTop: '12px', textAlign: 'center', backgroundColor: 'var(--youro-sage)', color: 'white' }}
                                                  >
                                                    Continue
                                                  </button>
                                                  {/* A patient who doesn't have a medication list or
                                                      their pharmacy's address to hand shouldn't be stuck
                                                      here. The server moves on from a question that was
                                                      left unanswered. */}
                                                  <button
                                                    type="button"
                                                    disabled={pharmacySaving}
                                                    onClick={() => {
                                                      // Held like Continue while a chosen pharmacy saves.
                                                      if (pharmacySaving) return;
                                                      const skipped = questionnaire[0];
                                                      const skippedId = skipped.questionId;
                                                      // Recorded as a blank answer, not left out: Previous
                                                      // undoes one entry per question shown, so a skipped
                                                      // question missing from the list made it discard the
                                                      // answer before it instead. The server ignores blank
                                                      // typed answers when filling in the medical profile.
                                                      setUserResponse(prev => [
                                                        ...prev.filter(r => r.qId !== skippedId),
                                                        { qId: skippedId, question: skipped.question, optionsData: [{ oId: -100, oName: "" }] },
                                                      ]);
                                                      setTextResponses(prev => {
                                                        const next = { ...prev };
                                                        delete next[skippedId];
                                                        return next;
                                                      });
                                                      setSelectedOption(0);
                                                      setQId(skippedId);
                                                      setIsBackNavigation(0);
                                                      setQuestionnaire([]);
                                                    }}
                                                    // Ghost tier (DESIGN_SYSTEM.md §5): Charcoal text, Mint hover.
                                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--youro-mint)"; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                                                    style={{ width: "100%", marginTop: "8px", padding: "10px", border: "none", borderRadius: "8px", background: "transparent", color: "var(--youro-charcoal)", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer" }}
                                                  >
                                                    Skip for now
                                                  </button>
                                                  {name === "Intake Form" && (
                                                    <p style={{ margin: "4px 0 0", fontSize: "0.8125rem", color: "var(--youro-muted)", textAlign: "center" }}>
                                                      Don't have this to hand? Skip it. You can add or change it later from your Profile.
                                                    </p>
                                                  )}
                                              </div>
                                              ) : (
                                                /* RENDER OPTIONS (RADIO OR CHECKBOX) */
                                                <>
                                                {questionnaire[0].options.map((option) => {
                                                  const isSelected = userResponse.find(r => r.qId === questionnaire[0].questionId)
                                                    ?.optionsData.some(opt => opt.oId === option.oId);

                                                  return (
                                                    <button
                                                      key={option.oId}
                                                      onClick={() => handleBackendOptionClick(option.oId, option.oName, isMultiOption)}
                                                      style={{ ...optionButtonStyle, borderColor: isSelected ? "#8fa87a" : "#e5e7eb", backgroundColor: isSelected ? "#cfe8d4" : "#ffffff", fontWeight: isSelected ? "600" : "400", transition: "all 0.2s ease" }}
                                                      onMouseEnter={handleMouseEnter}
                                                      onMouseLeave={(e) => handleMouseLeave(e, isSelected)}
                                                    >
                                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        {/* Custom visual indicator (Circle for radio, Square for checkbox) */}
                                                        {isMultiOption && (
                                                          <div style={{
                                                            width: '18px',
                                                            height: '18px',
                                                            border: '2px solid #8fa87a',
                                                            backgroundColor: isSelected ? '#8fa87a' : 'transparent',
                                                            borderRadius: '4px',
                                                            flexShrink: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                          }}>
                                                            {isSelected && <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'white' }} />}
                                                          </div>
                                                        )}
                                                        <span>{option.oName}</span>
                                                      </div>
                                                    </button>
                                                  )
                                                })}

                                                {isMultiOption && (
                                                  <button
                                                    onClick={() => {
                                                      const currentResponse = userResponse.find(r => r.qId === questionnaire[0].questionId);
                                                      if (!currentResponse || currentResponse.optionsData.length === 0) {
                                                        toast.info("Choose at least one option to continue.");
                                                        return;
                                                      }

                                                      // Ticking never set selected_option_id, so the previous
                                                      // question's option went with this one and a follow-up
                                                      // ("What type of cancer?") could never be reached.
                                                      setSelectedOption(followUpOptionId(
                                                        questionnaire[0],
                                                        currentResponse.optionsData.map(o => o.oId)
                                                      ));
                                                      setQId(questionnaire[0].questionId);
                                                      setIsBackNavigation(0);
                                                      setQuestionnaire([]);
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      e.currentTarget.style.transform = "scale(1.03)";
                                                      e.currentTarget.style.backgroundColor = 'var(--youro-coral)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = "scale(1)";
                                                        e.currentTarget.style.backgroundColor = '#8fa87a';
                                                    }}
                                                    style={{
                                                      ...optionButtonStyle,
                                                      marginTop: '12px',
                                                      textAlign: 'center',
                                                      backgroundColor: '#8fa87a',
                                                      color: 'white',
                                                      fontWeight: '600'
                                                    }}
                                                  >
                                                    Continue
                                                  </button>
                                                )}
                                                </>
                                              )
                                            ) : !name ? (
                                                // 2. RENDER HARDCODED OPTIONS (Hidden if 'name' exists)
                                                questions[step].options.map((option) => (
                                                    <button
                                                        key={option}
                                                        onClick={() => handleOptionClick(option)}
                                                        style={optionButtonStyle}
                                                        onMouseEnter={handleMouseEnter}
                                                        onMouseLeave={handleMouseLeave}
                                                    >
                                                        {option}
                                                    </button>
                                                ))
                                            ) : null}
                                        </div>
                                    </>
                                )}
                            </div>
                        </>
                    )
                    : showEmailCapture && emailSentTo ? (
                        /* --- Score emailed --- */
                        <div style={{ ...cardBase, padding: "32px", textAlign: "center", animation: "fadeIn 0.5s ease" }}>
                            <div style={{ width: "64px", height: "64px", backgroundColor: "var(--youro-mint)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--youro-sage)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path><rect x="2" y="4" width="20" height="16" rx="2"></rect></svg>
                            </div>
                            <h4 style={{ fontSize: "1.5rem", fontWeight: 700, margin: "0 0 8px 0" }}>Check your inbox</h4>
                            {/* Results are sent only once the address is confirmed:
                                the first email carries a link and nothing else. */}
                            <p style={{ color: "var(--youro-muted)", margin: "0 0 4px 0" }}>
                                If this is your first questionnaire with this address, we've sent a link to confirm it. Follow the link and we'll email your symptom score and what it means to
                            </p>
                            <p style={{ fontWeight: 600, color: "var(--youro-charcoal)", margin: "0 0 24px 0", wordBreak: "break-all" }}>{emailSentTo}</p>
                            <p style={{ fontSize: "0.875rem", color: "var(--youro-muted)", marginBottom: "24px" }}>
                                It can take a few minutes. If you don't see it, check your spam or junk folder.
                            </p>
                            <button
                                onClick={() => navigate("/signup")}
                                style={{
                                    width: "100%", backgroundColor: "var(--youro-coral)", color: "var(--youro-card)", padding: "12px", borderRadius: "var(--radius)", fontWeight: 600, border: "none", cursor: "pointer", marginBottom: "12px"
                                }}
                            >
                                Get Started with Youro
                            </button>
                            <button
                                onClick={() => navigate("/conditions")}
                                style={{
                                    width: "100%", backgroundColor: "transparent", color: "var(--youro-sky)", padding: "10px", borderRadius: "var(--radius)", fontWeight: 600, border: "none", cursor: "pointer"
                                }}
                            >
                                Back to conditions
                            </button>
                        </div>
                    )
                    : showEmailCapture ? (
                        /* --- Email Capture Component --- */
                        <div style={{ ...cardBase, padding: "32px", textAlign: "center", animation: "fadeIn 0.5s ease" }}>
                            <div style={{ width: "64px", height: "64px", backgroundColor: "rgba(255, 111, 97, 0.1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ff6f61" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path><rect x="2" y="4" width="20" height="16" rx="2"></rect></svg>
                            </div>
                            <h4 style={{ fontSize: "1.5rem", fontWeight: 700, margin: "0 0 8px 0" }}>Almost Done!</h4>
                            <p style={{ color: "#6b7280", marginBottom: "24px" }}>Enter your email address and we'll send you your symptom score and what it means.</p>

                            <div style={{ textAlign: "left", marginBottom: "16px" }}>
                                <label htmlFor="assessment-email" style={{ fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>Email Address</label>
                                <input
                                    id="assessment-email"
                                    type="email"
                                    autoComplete="email"
                                    placeholder="your.email@example.com"
                                    value={email}
                                    onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                                    onKeyDown={(e) => { if (e.key === "Enter") handleEmailSubmit(); }}
                                    aria-invalid={!!emailError}
                                    style={{
                                        width: "100%", padding: "10px", marginTop: "4px", borderRadius: "8px", border: emailError ? "1px solid var(--youro-error)" : "1px solid var(--youro-border)"
                                    }}
                                />
                                {emailError && <p role="alert" style={{ color: "var(--youro-error)", fontSize: "0.75rem", marginTop: "4px" }}>{emailError}</p>}
                            </div>

                            <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", textAlign: "left", fontSize: "0.875rem", color: "var(--youro-charcoal)", marginBottom: "20px", cursor: "pointer" }}>
                                <input
                                    type="checkbox"
                                    checked={marketingOptIn}
                                    onChange={(e) => setMarketingOptIn(e.target.checked)}
                                    style={{ width: "16px", height: "16px", marginTop: "2px", flex: "none", accentColor: "var(--youro-sage)", cursor: "pointer" }}
                                />
                                <span>{MARKETING_CONSENT_TEXT}</span>
                            </label>

                            <button
                                onClick={handleEmailSubmit}
                                disabled={emailing}
                                aria-busy={emailing}
                                style={{
                                    width: "100%", backgroundColor: "#ff6f61", color: "white", padding: "12px", borderRadius: "8px", fontWeight: 600, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px"
                                }}
                            >
                                Email My Score
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                            </button>
                        </div>
                    ) : (
                        /* --- Final Results Page (Step 9) --- */
                        <div style={{ animation: "fadeIn 0.5s ease" }}>

                            {/* Summary Card */}
                            <div style={{ ...cardBase, padding: "32px", textAlign: "center" }}>
                                <div style={{ width: "64px", height: "64px", backgroundColor: "#dcfce7", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.801 10A10 10 0 1 1 17 3.335" /><path d="m9 11 3 3L22 4" /></svg>
                                </div>
                                <h4 style={{ fontSize: "1.5rem", fontWeight: 700, margin: "0 0 8px 0" }}>Assessment Complete</h4>
                                <p style={{ color: "#6b7280", margin: 0 }}>Here are your personalized results and recommendations</p>
                            </div>

                            {/* Symptoms Card */}
                            {results.length > 0 && (
                              <div style={{ ...cardBase, padding: "24px" }}>
                                  <h4 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "4px" }}>Symptoms Identified</h4>
                                  <p style={{ fontSize: "0.875rem", color: "#6b7280", marginBottom: "20px" }}>Based on your responses, you may be experiencing the following conditions:</p>

                                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                    {results.map((item, idx) => (
                                      <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px", backgroundColor: "rgba(207, 232, 212, 0.7)", borderRadius: "8px", border: "1px solid #e5e7eb", transition: "transform 0.2s ease", cursor: "pointer", }}
                                        onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.02)"; e.currentTarget.style.backgroundColor = "rgba(207, 232, 212, 0.9)"; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.backgroundColor = "rgba(207, 232, 212, 0.7)"; }}
                                        {...clickable(() => { const path = urlMap[item.label]; if (path) navigate(path); })}
                                      >
                                        <span style={{ fontWeight: 500 }}>{item.label}</span>
                                        <span style={{ fontSize: "0.75rem", padding: "4px 10px", borderRadius: "99px", backgroundColor: item.color, color: item.textColor, fontWeight: 600, }} >
                                          {item.severity}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                              </div>
                            )}

                            {/* High Priority Card */}
                            <div style={{ ...cardBase, padding: "24px", backgroundColor: config.bgColor, borderColor: config.borderColor, border: `2px solid ${config.borderColor}`, transition: "all 0.3s ease" }}>
                                <h4 style={{ fontSize: "1.25rem", fontWeight: 700, color: config.titleColor, margin: "0 0 8px 0" }}>
                                    {config.title}
                                </h4>
                                <p style={{ color: "#4b5563", fontSize: "1rem", lineHeight: "1.5", marginBottom: "20px" }}>
                                    {config.description}
                                </p>
                                <h4 style={{ marginBottom: "0.75rem", fontWeight: "500" }}>Recommended next steps:</h4>
                                <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "16px" }}>
                                    {config.steps.map((text, i) => (
                                        <div key={i} style={{ display: "flex", alignItems: "start", gap: "10px", fontSize: "0.875rem", color: "#4b5563" }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style={{ color: "#00a63e", width: "1rem", height: "1rem", marginRight: "0.5rem", marginTop: "0.125rem", flexShrink: "0" }} aria-hidden="true"><path d="M21.801 10A10 10 0 1 1 17 3.335"></path><path d="m9 11 3 3L22 4"></path></svg>
                                            {text}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* --- BUTTONS --- */}
                            <div style={{ display: "flex", flexDirection: "row", gap: "12px", marginTop: "24px" }}>
                                <button style={{
                                    flex: 1, // Makes button take 50% width
                                    backgroundColor: "#ff6f61",
                                    color: "white",
                                    border: "none",
                                    height: "44px",
                                    borderRadius: "8px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "8px",
                                    whiteSpace: "nowrap", // Prevents text wrapping on small screens
                                    transition: "transform 0.2s ease"
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.02)"; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                                  onClick={() => navigate("/signup")}
                                >
                                    Schedule Consultation
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                                </button>

                                <button style={{
                                    flex: 1, // Makes button take 50% width
                                    backgroundColor: "transparent",
                                    color: "#2b7a9b",
                                    border: "1px solid #0ea5e9",
                                    height: "44px",
                                    borderRadius: "8px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    transition: "transform 0.2s ease"
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.02)"; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                                  onClick={() => navigate("/conditions")}
                                >
                                    Learn About Conditions
                                </button>
                            </div>

                        </div>
                    )}
                </div>
            </main>

            {/* The score.
                Not dismissible by clicking the backdrop: this is the answer to a
                dozen questions, and the only way past it is the button, which is
                also what performs the redirect that used to happen on a timer. */}
            {scoreResult && scoreTier && (
                <div
                    ref={scoreDialogRef}
                    tabIndex={-1}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="symptom-score-heading"
                    style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(46, 46, 46, 0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "24px",
                        zIndex: 2000,
                    }}
                >
                    <div style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "12px",
                        border: "1px solid #d6d3cc",
                        padding: "32px 28px",
                        maxWidth: "440px",
                        width: "100%",
                        maxHeight: "85vh",
                        overflowY: "auto",
                        textAlign: "center",
                    }}>
                        <p style={{ margin: 0, fontSize: "0.875rem", color: "#6f675c" }}>
                            {scoreResult.diagName || "Symptom Assessment"}
                        </p>

                        {/* Out of 100, and said so. The number alone invites the
                            obvious misreading — this scale runs the other way,
                            where a high score means symptoms interfere less. */}
                        <div style={{
                            width: "132px",
                            height: "132px",
                            borderRadius: "50%",
                            margin: "20px auto 0",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: scoreTier.bgColor,
                            border: `2px solid ${scoreTier.borderColor}`,
                        }}>
                            <span style={{ fontSize: "2.75rem", fontWeight: 700, lineHeight: 1.1, color: scoreTier.textColor }}>
                                {Math.round(Number(scoreResult.score))}
                            </span>
                            <span style={{ fontSize: "0.75rem", color: "#2e2e2e" }}>out of 100</span>
                        </div>

                        <h2
                            id="symptom-score-heading"
                            style={{ margin: "16px 0 0", fontSize: "1.25rem", color: scoreTier.textColor }}
                        >
                            {scoreTier.label}
                        </h2>

                        <p style={{ margin: "12px 0 0", fontSize: "1rem", lineHeight: 1.45, color: "#2e2e2e", textAlign: "left" }}>
                            {scoreTier.description}
                        </p>
                        {scoreTier.guidance && (
                            <p style={{ margin: "12px 0 0", fontSize: "1rem", fontWeight: 600, lineHeight: 1.45, color: "#2e2e2e", textAlign: "left" }}>
                                {scoreTier.guidance}
                            </p>
                        )}

                        <p style={{ margin: "16px 0 20px", fontSize: "0.875rem", lineHeight: 1.4, color: "#6f675c" }}>
                            Your provider sees this before your visit. You can follow it over time on your home page.
                        </p>

                        <button
                            style={{
                                width: "100%",
                                height: "44px",
                                backgroundColor: "#ff6f61",
                                color: "#ffffff",
                                border: "none",
                                borderRadius: "8px",
                                fontWeight: 600,
                                cursor: "pointer",
                            }}
                            onClick={() => {
                                setScoreResult(null);
                                navigate("/patient-home");
                            }}
                        >
                            Done
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Assessment;